import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormField } from "@tellescope/types-models";
import { isBookingBlockedByEligibility, isValueEmpty, responsesSatisfyConditions } from "../../utils";
import type { NextFieldLogicOptions } from "../../utils/types";
import { TellescopeFieldResponse } from "../../types";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";

export interface IsFormCompleteArgs {
  visibleFields: (FormField & { answer: TellescopeFieldResponse["answer"] })[];
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">> | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
  fieldErrors: Record<string, string | null>;
  logicOptions: NextFieldLogicOptions;
}

/**
 * Whether every visible field that needs an answer has a non-empty one with no recorded error.
 *
 * "Has an error" means the entry is present and non-null — an unvalidated field is not treated as
 * valid here.
 */
export const isFormComplete = ({
  visibleFields,
  fieldsMap,
  currentResponses,
  fieldErrors,
  logicOptions,
}: IsFormCompleteArgs): boolean => {
  const responsesArray = Object.values(currentResponses);

  return visibleFields.every((field) => {
    const fieldMetadata = fieldsMap?.[field.id];
    if (!fieldMetadata) return false;

    if (fieldMetadata.isOptional) return true;

    if (isBookingBlockedByEligibility(fieldMetadata, responsesArray)) return true;

    if (fieldMetadata.type === "Question Group") {
      const subFields = fieldMetadata.options?.subFields ?? [];
      return subFields.every((sf) => {
        const subFieldMeta = fieldsMap?.[sf.id];
        if (!subFieldMeta) return true;

        if (subFieldMeta.groupShowCondition && Object.keys(subFieldMeta.groupShowCondition).length) {
          const isVisible = responsesSatisfyConditions(responsesArray, subFieldMeta.groupShowCondition, logicOptions);
          if (!isVisible) return true;
        }

        if (subFieldMeta.isOptional) return true;

        const subResponse = currentResponses[sf.id];
        if (!subResponse) return false;

        const hasValue = !isValueEmpty(subResponse.answer.value);
        const hasError = fieldErrors[sf.id] !== null && fieldErrors[sf.id] !== undefined;
        return hasValue && !hasError;
      });
    }

    const response = currentResponses[field.id];
    if (!response) return false;

    const hasValue = !isValueEmpty(response.answer.value);
    const hasError = fieldErrors[field.id] !== null && fieldErrors[field.id] !== undefined;

    return hasValue && !hasError;
  });
};
